import { useMutation } from '@tanstack/react-query'
import { updateKycStatusAndGetAppCTA } from '../repository/MutualFundDetail'
import { useEffect, useRef } from 'react'
import { handleClose, postCTA, trackEvent } from '../utils/appBridge'
import { captureException } from '@sentry/react'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { EventNames } from '../types/misc.types'

const MAX_RETRIES = 6

// Utility functions
const logException = (error: string, errorType: string, extra: Record<string, any> = {}) => {
  captureException(new Error(error), {
    tags: {
      component: 'DigiLockerPage',
      errorType
    },
    extra: {
      ...extra,
      url: window.location.href,
      timestamp: new Date().toISOString()
    }
  })
}

// Component functions
const handleApiSuccess = (data: any, onError: (error: string) => void) => {
  const ctaData = data?.cta || data?.data?.cta
  if (ctaData) {
    postCTA(JSON.stringify(ctaData))
  } else {
    onError('no cta from response')
  }
}

const handleApiError = (error: any, retryCount: number, onRetry: () => void, onError: (error: string) => void) => {
  const statusCode = error?.response?.status || 'unknown'
  const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error'
  const errorString = `API failed ${statusCode}: ${errorMessage}`

  if (retryCount < MAX_RETRIES) {
    onRetry()
  } else {
    onError(errorString)
  }
}

const createErrorHandler = (retryCountRef: React.MutableRefObject<number>) => {
  return (errString: string) => {
    logException(errString, 'api_failure', {
      retryCount: retryCountRef.current
    })
    handleClose()
  }
}

const createRetryHandler = (retryCountRef: React.MutableRefObject<number>, mutate: () => void) => {
  return () => {
    retryCountRef.current += 1
    mutate()
  }
}

// Main component
export default function DGAadhaarPage() {
  const retryCountRef = useRef(0)

  const handleError = createErrorHandler(retryCountRef)

  const { mutate, isPending, isError, isSuccess } = useMutation({
    mutationFn: updateKycStatusAndGetAppCTA,
    onSuccess: (data) => {
      handleApiSuccess(data, handleError)
    },
    onError: (error: any) => {
      const handleRetry = createRetryHandler(retryCountRef, mutate)
      handleApiError(error, retryCountRef.current, handleRetry, handleError)
    }
  })

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        eventId: 'digilocker_reached',
        screenType: 'MF_DIGILOCKER_PAGE'
      }
    })

    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        eventId: 'digilocker_called',
        screenType: 'MF_DIGILOCKER_PAGE'
      }
    })

    mutate()
  }, [])

  // Render logic
  if (isPending) {
    return <LoadingSpinner />
  }

  if (isError) {
    return (
      <div className='h-screen overflow-y-scroll no-scrollbar'>
        <div className='flex min-h-screen items-center flex-col justify-center text-red-500 p-10'>
          <p className='text-center'>
            Something went wrong. <br /> Please try again.
          </p>
        </div>
      </div>
    )
  }

  if (isSuccess) {
    return <LoadingSpinner />
  }

  return <LoadingSpinner />
}
